import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';
import React, {useRef, useState} from 'react';

import isArchived from 'archive/isArchived';
import {LabAutomationFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isConnectFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import {Status as AutomationFileStatusGraphQL} from 'graphql-client/__generated/graphqlGlobalTypes';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {getFiltersFromConnectionIds} from 'instruments/connection-files/ConnectionFilesTable';
import ImportConnectionFilesModal from 'instruments/connection-files/ImportConnectionFilesModal';
import * as ConnectFilesDAO from 'instruments/connections/ConnectFilesDAO';
import {
  ConnectionStatus,
  InstrumentServiceLoadStatus,
  InstrumentServiceDetailsLoader,
} from 'instruments/connections/ConnectionConstants';
import BrowseConnectionFilesModal, {
  BrowseConnectionFilesSubmitResult,
} from 'instruments/instrument-data/BrowseConnectionFilesModal';
import {
  InstrumentQueryStatus,
  isDirectRequestAdapter,
} from 'instruments/instrument-data/InstrumentDataConstants';
import {
  createCopyObjectInstrumentQueryFromRun,
  pollInstrumentQueryStatus,
  pollUntil,
} from 'instruments/instrument-data/InstrumentDataDAO';
import {AutomationOutputFileType} from 'lab-automation/automation-files/LabAutomationConstants';
import {FromServerRunOutputSamplesTable} from 'lab-automation/output-samples/RunOutputSamplesTableConstants';
import {AutomationOutputFileTransformDataType} from 'lab-automation/settings/RunSchemaSpecConstants';
import waitForEntrySaved from 'lab-automation/utils/waitForEntrySaved';
import {FromServerResultsTable} from 'notebook/integrations/results/ResultsTableConstants';
import {EditorPropertiesContext} from 'notebook/notes/core-ui/NoteContext';
import notifications from 'notifications';
import * as BlobActions from 'schemas/tags/BlobActions';
import BlobInputModal from 'schemas/tags/BlobInputModal';
import * as GlobalSearchActions from 'search/GlobalSearchActions';
import {GlobalSearchResponse} from 'search/GlobalSearchDAO';
import {FilterKey, Operator, SearchType} from 'search/SearchConstants';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import runModal from 'ui-library/runModal';
// @ts-expect-error
// eslint-disable-next-line unused-imports/no-unused-imports
import BenchlingData from 'util/drag-and-drop/BenchlingData';
import DropTarget from 'util/drag-and-drop/DropTarget';
import * as modals from 'util/Modals';
import {asJQueryPromise} from 'util/Promises';
import {ElementDragEvent} from 'util/Types';

import {FileListingGraphQL} from '../../../graphql-fragments/files/__generated/FileListingGraphQL';
import {AutomationOutputFileConfigGraphQL} from '../../../graphql-fragments/lab-automation/__generated/AutomationOutputFileConfigGraphQL';
import {AutomationOutputFileGraphQL} from '../../../graphql-fragments/lab-automation/__generated/AutomationOutputFileGraphQL';
import {
  UploadAutomationOutputFileButtonQuery,
  UploadAutomationOutputFileButtonQueryVariables,
} from './__generated/UploadAutomationOutputFileButtonQuery';
import {
  processAutomationOutputFile,
  ProcessAutomationOutputFileResponse,
  setAutomationFileStatus,
} from './AutomationFileActions';
import * as AutomationFileDAO from './AutomationFileDAO';
import {PROCESS_ALL_AUTOMATION_OUTPUT_FILES_QUERY} from './ProcessAllAutomationOutputFilesButton';

export const UPLOAD_AUTOMATION_OUTPUT_FILE_BUTTON_QUERY: DocumentNode = gql`
  query UploadAutomationOutputFileButtonQuery($connectionId: StrictString!)
  @skipAudit
  @engComponent(name: INSTRUMENTS) {
    connection(id: $connectionId) {
      ... on ConnectionNode {
        id
        name
        ...HasArchiveRecordGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('HasArchiveRecord').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface UploadAutomationOutputFileButtonProps {
  runId: string | null;
  configName: string | null;
  automationOutputFileConfig?: AutomationOutputFileConfigGraphQL | null;
  isConnectionRun: boolean;
  connectionId: string | null;
  instrumentServiceDetails: InstrumentServiceDetailsLoader;
  disabled: boolean;
  disabledTooltip?: string;
  updateAutomationOutputFiles: (
    automationOutputFiles: Array<AutomationOutputFileGraphQL>,
    structuredTables?: Array<FromServerRunOutputSamplesTable | FromServerResultsTable>
  ) => void;
  previousFileNames: Set<string>;
  outputFileType?: AutomationOutputFileType;
  onRequestClose?: () => void;
  skipRowsWithErrors?: boolean;
  folderId?: string | null;
  locationId?: string | null;
  timezone?: string | null;
}

interface CopyObjectResponseParams {
  path?: string;
}

function markAsRunning(outputFiles: Array<AutomationOutputFileGraphQL>): Array<AutomationOutputFileGraphQL> {
  return outputFiles.map((outputFile) => ({
    ...outputFile,
    status: AutomationFileStatusGraphQL.running,
  }));
}

const UploadAutomationOutputFileButton: React.FunctionComponent<UploadAutomationOutputFileButtonProps> = (
  props: UploadAutomationOutputFileButtonProps
): JSX.Element => {
  const [isUploading, setIsUploading] = useState(false);
  const allowMultiple = props.outputFileType !== AutomationOutputFileType.AMENDED_ERRORS_FILE;
  const updateHandlerRef = useRef(props.updateAutomationOutputFiles);
  updateHandlerRef.current = props.updateAutomationOutputFiles;
  const {status: instrumentServiceStatus, data: instrumentServiceDetails} = props.instrumentServiceDetails;

  const uploadBlobsToOutputFile = async (
    blobOrFileIds: Array<string>,
    entryId?: string,
    markSuccess?: boolean,
    parentFileIds?: Array<string>
  ): Promise<Array<AutomationOutputFileGraphQL> | null> => {
    if (blobOrFileIds.length === 0) {
      return null;
    }
    const saved = entryId != null ? await waitForEntrySaved(entryId) : true;
    if (saved) {
      const response = await AutomationFileDAO.bulkUploadAutomationOutputFileBlobs({
        runId: props.runId!,
        configName: props.configName!,
        blobOrFileIds,
        outputFileType: props.outputFileType,
        markSuccess: Boolean(markSuccess),
        parentFileIds,
        folderId: props.folderId ?? undefined,
        locationId: props.locationId ?? undefined,
        timezone: props.timezone ?? undefined,
        // When file chunking is enabled, enable skip rows with errors by default. This will automatically
        // check the checkbox on the UI, which can be disabled by a user if desired. When chunking is
        // not enabled, we do not present the skip rows with errors option, so leave false.
        skipRowsWithErrors: props.skipRowsWithErrors ?? BenchlingConfig.ENABLE_LAB_AUTOMATION_FILE_CHUNKING,
      });
      return response.automationOutputFiles;
    }
    return null;
  };
  const queryResult = useSkippableBenchlingQuery<
    UploadAutomationOutputFileButtonQuery,
    UploadAutomationOutputFileButtonQueryVariables
  >(UPLOAD_AUTOMATION_OUTPUT_FILE_BUTTON_QUERY, {
    variables: {connectionId: props.connectionId!},
    skip: props.connectionId == null,
  });
  const connection = queryResult.type === QueryResultType.SUCCESS ? queryResult.data.connection : null;
  const isConnectionArchived = connection != null && !isInaccessible(connection) && isArchived(connection);

  const uploadFiles = (
    props: UploadAutomationOutputFileButtonProps,
    files: Array<File>,
    entryId?: string
  ): JQueryPromise<void> => {
    setIsUploading(true);
    return BlobActions.uploadFilesAsBlobs(files, {
      shouldGeneratePreviewFile: false,
      onBlobsCreated: (newBlobs) => {
        uploadBlobsToOutputFile(
          newBlobs.map((blob) => blob.id),
          entryId
        ).then((newFiles) => {
          if (newFiles) {
            props.updateAutomationOutputFiles(newFiles);
          }
        });
      },
    }).done(() => {
      setIsUploading(false);
    });
  };

  const onUploadFiles = (files: Array<File>, entryId?: string): JQueryPromise<void> => {
    if (!files || files.length === 0) {
      return asJQueryPromise(Promise.resolve()); // Empty Promise
    }
    const existingFileNames = files
      .map((file) => file.name)
      .filter((filename) => props.previousFileNames.has(filename));
    if (existingFileNames.length) {
      const deferred: JQueryDeferred<void> = $.Deferred();
      modals
        .confirm(
          'Confirm',
          `Files with the following names have already been uploaded:
            ${existingFileNames.join('\n')}
             Would you like to continue?`,
          ButtonStyle.PRIMARY,
          {
            promiseCompatible: true,
            enableRedesign: true,
          }
        )
        .then((confirmed) => {
          if (confirmed) {
            $.when(uploadFiles(props, files, entryId)).done(() => deferred.resolve());
          } else {
            // Resolve and close on cancel
            deferred.resolve();
          }
        });
      return deferred.promise();
    }
    // Don't confirm on first upload
    return uploadFiles(props, files, entryId);
  };

  const runBlobModal = (fileId?: string): void => {
    runModal(BlobInputModal, {
      allowMultiple,
      onUploadFiles: (files: Array<File>) => onUploadFiles(files, fileId),
      onRequestClose: () => {
        if (props.onRequestClose) {
          props.onRequestClose();
        }
      },
    });
  };

  const createFilesFromBlobs = async (
    blobIds: Array<string>,
    sourceFiles: Array<FileListingGraphQL>,
    transformDataType: AutomationOutputFileTransformDataType
  ): Promise<Array<{fileId: string}>> =>
    await Promise.all(
      blobIds.map(
        async (blobId, i) =>
          await ConnectFilesDAO.createFileFromBlob(
            blobId,
            sourceFiles[i].folder.id,
            // NOTE: connectionId is guaranteed to be set at this point in the modal.
            props.connectionId ?? '',
            undefined,
            transformDataType
          )
      )
    );

  const convertOriginaltoCSV = async (files: Array<FileListingGraphQL>): Promise<Array<string> | null> => {
    const transformDataType = props.automationOutputFileConfig?.transformDataType;
    // If transform type is ORIGINAL - nothing to do - simply convert files to blobs.
    if (transformDataType === AutomationOutputFileTransformDataType.ORIGINAL) {
      return files.map((file) => file.id);
    }

    // If instrument service details are unavailable or haven't loaded yet, fall back to treating
    // files as ORIGINAL format rather than showing confusing errors to users.
    if (instrumentServiceStatus === InstrumentServiceLoadStatus.UNAVAILABLE || !instrumentServiceDetails) {
      return files.map((file) => file.id);
    }
    // If the connection is a file watcher and no transformDataType is set, default to ORIGINAL.
    // TODO(BNCH-122768): backfill all file-watcher-adpater AOF configs with no transform type set to ORIGINAL.
    if (instrumentServiceDetails.adapterId === 'file-watcher-adapter' && !transformDataType) {
      return files.map((file) => file.id);
    }
    // TODO(BNCH-122768): these first two error checks are impossible for new runs now that we require transform
    // type to be set. If we run a script to auto-set the transform to a value (or confirm that all runs have
    // been migrated we could remove these two conditions.
    if (!transformDataType) {
      notifications.error(
        'Please set a default instrument data type on the output file configuration for this run schema, sync run data, and retry.'
      );
      return null;
    }
    // Special case: Chromeleon files are already in ASM format, and AVEVA PI uses
    // time series CSV parsing — neither needs an allotropy vendor for conversion.
    const skipVendorCheck =
      instrumentServiceDetails.adapterId === 'chromeleon-adapter' ||
      instrumentServiceDetails.adapterId === 'aveva-pi-adapter';
    if (
      !instrumentServiceDetails.allotropyVendor &&
      transformDataType !== AutomationOutputFileTransformDataType.CUSTOM &&
      !skipVendorCheck
    ) {
      notifications.error(
        'Connection does not have an instrument type setting, please set the default instrument data type on the output file configuration for this run schema to "Original", sync run data, and retry.'
      );
      return null;
    }

    // Convert file -> ASM -> CSV
    const vendor = instrumentServiceDetails.allotropyVendor;
    const jsonMapperConfigJson =
      transformDataType === AutomationOutputFileTransformDataType.CUSTOM
        ? props.automationOutputFileConfig?.jsonMapperConfigJson
        : undefined;
    const blobIds = await Promise.all(
      files.map(
        async (file) =>
          (
            await ConnectFilesDAO.convertToCsv(
              file.id,
              transformDataType,
              jsonMapperConfigJson,
              file.name,
              vendor,
              props.connectionId ?? null
            )
          )[0].blobId
      )
    );
    const fileResult = await createFilesFromBlobs(
      blobIds,
      files,
      transformDataType as AutomationOutputFileTransformDataType
    );
    return fileResult.map((r) => r.fileId);
  };

  const processFiles = (outputFiles: Array<AutomationOutputFileGraphQL>, entryId?: string): void => {
    for (const outputFile of outputFiles) {
      setAutomationFileStatus(outputFile.id, AutomationFileStatusGraphQL.running);
      try {
        processAutomationOutputFile(outputFile.id, entryId).then(
          (response: ProcessAutomationOutputFileResponse) => {
            updateHandlerRef.current([response.automationOutputFile], response.structuredTables);
            doQuerySafe(
              {query: PROCESS_ALL_AUTOMATION_OUTPUT_FILES_QUERY},
              {
                variables: {automationOutputFileIds: [response.automationOutputFile.id]},
                fetchPolicy: 'network-only',
              }
            );
            setAutomationFileStatus(response.automationOutputFile.id, response.automationOutputFile.status);
          }
        );
      } catch (err) {
        setAutomationFileStatus(outputFile.id, outputFile.status);
        throw err;
      }
    }
  };

  const runBrowseConnectionFilesModal = (entryId?: string): void => {
    if (!props.automationOutputFileConfig) {
      notifications.error(
        'Cannot run browse connection files modal without an automation output file config'
      );
      return;
    }
    runModal(BrowseConnectionFilesModal, {
      connectionId: props.connectionId!,
      runId: props.runId ?? undefined,
      allowMultiple,
      onRequestSubmit: async (result: BrowseConnectionFilesSubmitResult) => {
        setIsUploading(true);
        try {
          // Search data catalog for newly retrieved files (from COPY_OBJECT)
          const newFiles = await Promise.all(result.newFileNames.map((filename) => searchForFile(filename)));
          // Combine with existing data catalog files selected by the user
          const allFiles = [...newFiles, ...result.existingFiles];
          if (allFiles.length === 0) {
            return;
          }

          const convertedFileIds = await convertOriginaltoCSV(allFiles);
          if (convertedFileIds) {
            const newOutputFiles = await uploadBlobsToOutputFile(
              convertedFileIds,
              entryId,
              false,
              allFiles.map((file) => file.id)
            );
            if (newOutputFiles) {
              processFiles(newOutputFiles, entryId);
              props.updateAutomationOutputFiles(markAsRunning(newOutputFiles));
            }
          }
        } catch (error) {
          if (error instanceof Error) {
            notifications.error(error.message);
          } else {
            throw error;
          }
        } finally {
          setIsUploading(false);
        }
      },
      onRequestClose: props.onRequestClose,
    });
  };

  const runConnectionFilesModal = (entryId?: string): void => {
    if (!props.automationOutputFileConfig) {
      notifications.error(
        'Can not run import instrument data modal without an automation output file config'
      );
      return;
    }
    runModal(ImportConnectionFilesModal, {
      automationOutputFileConfig: graphQLToREST(
        'AutomationOutputFileConfig',
        props.automationOutputFileConfig
      ),
      connectionId: props.connectionId ?? undefined,
      allowMultiple,
      onRequestSubmit: async (files: Array<FileListingGraphQL>) => {
        // If using direct file attachment feature, the attachAndProcessFiles callback handles both attaching
        // and processing files, so there is nothing to do here. This callback is still passed as a noop
        // so that the modal can close when onRequestSubmit is called.
        // TODO(BNCH-108120): add support for attaching files to all users of the modal, and replace the
        // attachAndProcessFiles callback with onRequestSubmit.
        if (
          BenchlingConfig.ENABLE_BENCHLING_CONNECT_ATTACH_FILES_TO_RUNS ||
          (props.automationOutputFileConfig && props.automationOutputFileConfig.shouldAllowAttachment)
        ) {
          return;
        }
        setIsUploading(true);
        try {
          const convertedFileIds = await convertOriginaltoCSV(files);
          if (convertedFileIds) {
            const newOutputFiles = await uploadBlobsToOutputFile(
              convertedFileIds,
              entryId,
              false,
              files.map((file) => file.id)
            );
            if (newOutputFiles) {
              processFiles(newOutputFiles, entryId);
              props.updateAutomationOutputFiles(markAsRunning(newOutputFiles));
            }
          }
        } finally {
          setIsUploading(false);
        }
      },
      attachAndProcessFiles:
        BenchlingConfig.ENABLE_BENCHLING_CONNECT_ATTACH_FILES_TO_RUNS ||
        props.automationOutputFileConfig.shouldAllowAttachment
          ? async (filesToAttach: Array<FileListingGraphQL>, filesToProcess: Array<FileListingGraphQL>) => {
              setIsUploading(true);
              try {
                const newAttachFiles = await uploadBlobsToOutputFile(
                  filesToAttach.map((file) => file.id),
                  entryId,
                  true
                );
                const convertedFileIds = await convertOriginaltoCSV(filesToProcess);
                const newProcessFiles = convertedFileIds
                  ? await uploadBlobsToOutputFile(
                      convertedFileIds,
                      entryId,
                      false,
                      filesToProcess.map((file) => file.id)
                    )
                  : null;
                if (newAttachFiles || newProcessFiles) {
                  const runningNewProcessFiles = newProcessFiles ? markAsRunning(newProcessFiles) : [];
                  props.updateAutomationOutputFiles([...(newAttachFiles || []), ...runningNewProcessFiles]);
                }
                if (newAttachFiles) {
                  for (const outputFile of newAttachFiles) {
                    setAutomationFileStatus(outputFile.id, AutomationFileStatusGraphQL.succeeded);
                  }
                }
                if (newProcessFiles) {
                  processFiles(newProcessFiles, entryId);
                }
              } finally {
                setIsUploading(false);
              }
            }
          : undefined,
      onRequestClose: props.onRequestClose,
      instrumentServiceDetails,
    });
  };

  const searchForFile = async (filePath: string): Promise<FileListingGraphQL> => {
    if (!props.connectionId) {
      throw new Error('Cannot search for file without a connection ID.');
    }
    const connectionId = props.connectionId;
    // The COPY_OBJECT response may return a full path (e.g. chrom://host/path/file.seq).
    // The data catalog indexes files by basename only.
    const basename = filePath.split('/').pop() ?? filePath;
    const result = await pollUntil(
      async () => {
        const filters = getFiltersFromConnectionIds([connectionId]);
        filters.push({
          key: FilterKey.NAMES,
          operator: Operator.IS_ONE_OF,
          value: [basename],
        });
        return (await GlobalSearchActions.doGlobalSearch(
          {
            offset: 0,
            limit: 1,
            sorts: [],
            filters,
            query: '',
          },
          SearchType.GLOBAL,
          'lab_automation:OUTPUT_FILE_POLL'
        )) as GlobalSearchResponse;
      },
      (response: GlobalSearchResponse) => response.ids.length === 1,
      3 * 1000, // 3 seconds between retries
      30 * 1000 // 30 seconds max wait
    );
    return result.filesById[result.ids[0]];
  };

  const sendCopyObjectCommand = async (entryId?: string): Promise<void> => {
    if (props.connectionId && props.runId) {
      setIsUploading(true);
      try {
        const instrumentQueryId = (
          await createCopyObjectInstrumentQueryFromRun(props.connectionId, props.runId)
        ).instrumentQueryId;

        const queryResponse = await pollInstrumentQueryStatus(instrumentQueryId);
        if (queryResponse.status !== InstrumentQueryStatus.DONE) {
          notifications.error(`An error occured retrieving data: ${queryResponse.info}`);
          return;
        }

        const filename = (queryResponse.params as CopyObjectResponseParams)?.path;
        if (!filename) {
          notifications.error('An error occured querying data. Please contact Benchling Support.');
          return;
        }
        const files = [await searchForFile(filename)];
        const convertedFileIds = await convertOriginaltoCSV(files);
        if (convertedFileIds) {
          const newOutputFiles = await uploadBlobsToOutputFile(
            convertedFileIds,
            entryId,
            false,
            files.map((file) => file.id)
          );
          if (newOutputFiles) {
            processFiles(newOutputFiles, entryId);
            props.updateAutomationOutputFiles(markAsRunning(newOutputFiles));
          }
        }
      } catch (error) {
        if (error instanceof Error) {
          notifications.error(error.message);
        } else {
          throw error;
        }
      } finally {
        setIsUploading(false);
      }
    }
  };

  const isConnectionImportButton =
    props.isConnectionRun && props.outputFileType !== AutomationOutputFileType.AMENDED_ERRORS_FILE;

  // TODO(nstender): generalize this to adapters that don't browse files and instead rely on run fields.
  // or just replace this whole thing with a better UI.
  const shouldSendCopyObject =
    isConnectionImportButton &&
    instrumentServiceDetails &&
    isDirectRequestAdapter(instrumentServiceDetails.adapterId);
  const buttonText = isConnectionImportButton ? 'Retrieve data' : 'Upload file';

  const isDisabledConnectionButton =
    isConnectionImportButton &&
    !isConnectFeatureEnabled({feature: LabAutomationFeature.LAB_AUTOMATION_DISCOVER});
  const isUnavailableDirectRequestConnection =
    shouldSendCopyObject &&
    instrumentServiceDetails &&
    instrumentServiceDetails.status.value !== ConnectionStatus.AVAILABLE;
  const isInstrumentServiceLoading = instrumentServiceStatus === InstrumentServiceLoadStatus.LOADING;

  const buttonDisabled =
    props.disabled ||
    isConnectionArchived ||
    isDisabledConnectionButton ||
    isUnavailableDirectRequestConnection ||
    isInstrumentServiceLoading;

  let disabledTooltipText = props.disabledTooltip ?? '';
  if (isConnectionArchived) {
    disabledTooltipText = 'Files cannot be retrieved since the connection has been archived.';
  } else if (isDisabledConnectionButton) {
    disabledTooltipText =
      'Cannot retrieve data from connection because Connect feature is disabled, please contact your Benchling administrator.';
  } else if (isUnavailableDirectRequestConnection) {
    disabledTooltipText = 'Data cannot be retrieved while connection is unavailable.';
  } else if (isInstrumentServiceLoading) {
    disabledTooltipText = 'Loading connection details...';
  }

  return (
    <EditorPropertiesContext.Consumer>
      {({fileId}) => (
        <DropTarget
          shouldCaptureDrop={!isConnectionImportButton}
          validateDrop={(): true | null => (props.disabled ? true : null)}
          mimeTypes={['Files']}
          dropEffect="link"
          targetDesc="output files"
          onDrop={(e: ElementDragEvent): void => {
            let files: FileList | Array<File> = e.dataTransfer.files;
            if (files.length) {
              if (!allowMultiple) {
                files = [files[0]];
              }
              onUploadFiles(Array.from(files), fileId);
            }
          }}
        >
          <Tooltip title={disabledTooltipText} disabled={!buttonDisabled}>
            <Button
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              size={ButtonSize.MINI}
              fullWidth={true}
              text={isUploading ? (shouldSendCopyObject ? 'Retrieving...' : 'Uploading...') : buttonText}
              icon={isUploading ? <BenchlingIcon icon="refresh" className="glyphicon-spin" /> : 'upload'}
              disabled={buttonDisabled}
              textClassName="button-text--nowrap"
              onClick={() => {
                if (shouldSendCopyObject) {
                  sendCopyObjectCommand(fileId);
                } else if (isConnectionImportButton) {
                  if (instrumentServiceDetails && !instrumentServiceDetails.isFileSyncing) {
                    // Non-auto-syncing connections: browse + COPY_OBJECT (handles login internally)
                    runBrowseConnectionFilesModal(fileId);
                  } else {
                    // Auto-syncing connections or instrument service not loaded: data catalog view
                    runConnectionFilesModal(fileId);
                  }
                } else {
                  runBlobModal(fileId);
                }
              }}
              dataTestAttrs={{component: 'UploadAutomationOutputFileButton', element: 'uploadButton'}}
            />
          </Tooltip>
        </DropTarget>
      )}
    </EditorPropertiesContext.Consumer>
  );
};

export default UploadAutomationOutputFileButton;
